import { useEffect, useState } from 'react';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';
import { refreshDatabaseSchema } from '../utils/schemaRefresh';

export const useInitialData = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isLoading: authLoading } = useAuth();
  
  const { 
    fetchClients, 
    fetchSuppliers, 
    fetchInvoices, 
    fetchPayments,
    fetchProductions,
    fetchProductionTasks,
    fetchSampleTasks,
    fetchSamples,
    fetchExpress,
    fetchExpressItems,
    fetchExpressStock,
    fetchExpressStockMovements,
    fetchExpressSamples,
    fetchExpressSampleItems,
    fetchTodoItems,
    fetchTaskBoards,
    fetchTaskResponsibles,
    fetchTaskCategories,
    fetchTaskProjects,
    fetchExpenseCategories,
    fetchExpenses,
    setError: setAppError,
    fetchSalespersons
  } = useAppStore();

  // Helper function to safely execute fetch operations with timeout
  const safeFetch = async (fetchFn: () => Promise<void>, name: string) => {
    try {
      // Agregar timeout individual de 30 segundos por consulta
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`Timeout al cargar ${name}`)), 30000);
      });
      
      await Promise.race([fetchFn(), timeoutPromise]);
    } catch (error) {
      console.error(`Error fetching ${name}:`, error);
      // Log detailed error information for debugging
      if (error instanceof Error) {
        console.error(`${name} fetch error details:`, {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
      }
      // Don't throw the error so other fetches can continue
      // Solo loguear el error pero no detener el proceso
    }
  };
  useEffect(() => {
    // Wait for auth to finish loading before attempting to load data
    if (authLoading) {
      return;
    }
    
    const loadData = async () => {
      // If no user is logged in, don't try to fetch data
      if (!user) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Set a timeout to prevent infinite loading
        // Aumentado a 60 segundos para dar más tiempo a las consultas pesadas
        const timeoutId = setTimeout(() => {
          setIsLoading(false);
          setError('Tiempo de carga excedido. Verifique su conexión a internet e intente nuevamente.');
          setAppError('Tiempo de carga excedido. Verifique su conexión a internet e intente nuevamente.');
        }, 60000); // 60 seconds timeout
        
        // First refresh the database schema (with error handling)
        try {
          await refreshDatabaseSchema();
        } catch (error) {
          console.error('Error refreshing database schema:', error);
          // Continue with data fetching even if schema refresh fails
        }
        
        // Then fetch clients as they are required by other components
        try {
          await fetchClients();
        } catch (error) {
          console.error('Error fetching clients:', error);
          setError('Error al conectar con la base de datos. Verifique su conexión a internet.');
          clearTimeout(timeoutId);
          setIsLoading(false);
          return;
        }
        
        // Then fetch the rest of the data in parallel
        await Promise.all([
          safeFetch(fetchSuppliers, 'suppliers'),
          safeFetch(fetchInvoices, 'invoices'),
          safeFetch(fetchPayments, 'payments'),
          safeFetch(fetchProductions, 'productions'),
          safeFetch(fetchProductionTasks, 'production tasks'),
          safeFetch(fetchSampleTasks, 'sample tasks'),
          safeFetch(fetchSamples, 'samples'),
          safeFetch(fetchExpress, 'express orders'),
          safeFetch(fetchExpressItems, 'express items'),
          safeFetch(fetchExpressStock, 'express stock'),
          safeFetch(fetchExpressStockMovements, 'express stock movements'),
          safeFetch(fetchExpressSamples, 'express samples'),
          safeFetch(fetchExpressSampleItems, 'express sample items'),
          safeFetch(fetchExpenseCategories, 'expense categories'),
          safeFetch(fetchExpenses, 'expenses'),
          safeFetch(fetchTodoItems, 'todo items'),
          safeFetch(fetchTaskBoards, 'task boards'),
          safeFetch(fetchTaskResponsibles, 'task responsibles'),
          safeFetch(fetchTaskCategories, 'task categories'),
          safeFetch(fetchTaskProjects, 'task projects'),
          safeFetch(fetchSalespersons, 'salespersons')
        ]);
        
        // Clear the timeout since data loaded successfully
        clearTimeout(timeoutId);
      } catch (error: any) {
        const errorMessage = error.message?.includes('fetch') 
          ? 'Error de conexión. Verifique su conexión a internet e intente nuevamente.'
          : error.message || 'Error al cargar los datos iniciales';
        setError(errorMessage);
        setAppError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [
    user,
    authLoading,
    fetchClients, 
    fetchSuppliers, 
    fetchInvoices, 
    fetchPayments, 
    fetchProductions,
    fetchProductionTasks,
    fetchSampleTasks,
    fetchSamples,
    fetchExpress,
    fetchExpressItems,
    fetchExpressStock,
    fetchExpressStockMovements,
    fetchExpressSamples,
    fetchExpressSampleItems,
    fetchTodoItems,
    fetchTaskBoards,
    fetchTaskResponsibles,
    fetchTaskCategories,
    fetchTaskProjects,
    fetchExpenseCategories,
    fetchExpenses,
    setAppError,
    fetchSalespersons
  ]);

  return { isLoading, error };
};